import { nanoid } from "nanoid";
import {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useToggle } from "@touchtech/web-hooks";

import { ToastType, showToast } from "@is/components/Toasts";
import {
  DeviceType,
  InstoreHttpHeaderNames,
  LocalStorageKeys,
  ROUTES,
  TranslationContext,
} from "@is/constants";
import { ViewStateContext } from "@is/contexts";
import { useRegisterDevice } from "@is/hooks";
import { LocalStorageService } from "@is/services";
import { api } from "@is/services/api";
import { waitForDeviceActive } from "@is/utils";

const useManageNameDevice = () => {
  const { t } = useTranslation(TranslationContext.DeviceRegistration);
  const [showSpinner, setShowSpinner] = useState(false);

  const [showScannerSelectionModal, setShowScannerSelectionModal] =
    useToggle(false);

  const navigate = useNavigate();
  const { device, deviceName, selectedStore, updateViewState } =
    useContext(ViewStateContext).registration;

  const [{ isPending: isRegistrationPending, data, error }, registerDevice] =
    useRegisterDevice();

  const handleCloseScannerSelectionModal = useCallback(() => {
    setShowScannerSelectionModal(false);
    setShowSpinner(true);
  }, [setShowScannerSelectionModal]);

  useEffect(() => {
    if (error) {
      showToast({
        title: t("Sorry for the delay - please try again later."),
        message: t(
          "We're currently experiencing some delays in our system. Please try again later, and if the issue persists contact our support team for assistance."
        ),
        type: ToastType.Error,
      });
    }
  }, [error, t]);

  useEffect(() => {
    if (data?.id) {
      const deviceId = data.id;

      LocalStorageService.setItem(LocalStorageKeys.DeviceId, deviceId);
      api.setHeader(InstoreHttpHeaderNames.DeviceId, deviceId);

      if (device?.type === DeviceType.Tablet) {
        setShowSpinner(true);
      } else {
        setShowScannerSelectionModal(true);
      }
    }
  }, [data, device?.type, setShowScannerSelectionModal]);

  // Clean up partial registration state and send the user back to the
  // start so they can try again.
  const handleVerificationFailure = useCallback(() => {
    LocalStorageService.removeItem(LocalStorageKeys.DeviceId);
    LocalStorageService.removeItem(LocalStorageKeys.StoreId);
    LocalStorageService.removeItem(LocalStorageKeys.SessionId);
    LocalStorageService.removeItem(LocalStorageKeys.SearchId);

    const startUrl = LocalStorageService.getItem(LocalStorageKeys.StartUrl);
    if (startUrl) {
      window.location.href = startUrl;
    } else {
      const tenantId = LocalStorageService.getItem(LocalStorageKeys.TenantId);
      window.location.href = `/instore?t=${tenantId}`;
    }
  }, []);

  // Single source of truth for "verify device is active on the backend
  // before navigating to EntryView". Triggers whenever the spinner is
  // shown and we have a deviceId — covers both Tablet (auto) and
  // Counter (after scanner modal closes) flows.
  useEffect(() => {
    if (!showSpinner || !data?.id || !selectedStore?.id) {
      return;
    }

    let cancelled = false;

    const verify = async () => {
      const isActive = await waitForDeviceActive(selectedStore.id, data.id);
      if (cancelled) return;

      if (isActive) {
        navigate(ROUTES.EntryView);
        setShowSpinner(false);
      } else {
        handleVerificationFailure();
      }
    };

    verify();

    return () => {
      cancelled = true;
    };
  }, [
    showSpinner,
    data?.id,
    selectedStore?.id,
    navigate,
    handleVerificationFailure,
  ]);

  const handleChangeDeviceName = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      updateViewState({ deviceName: event.target.value });
    },
    [updateViewState]
  );

  const handleConfirm = useCallback(async () => {
    const data = {
      deviceId: nanoid(),
      storeId: selectedStore?.id,
      name: deviceName || device?.name,
      type: device?.type,
    };

    registerDevice(data);
  }, [device, selectedStore, deviceName, registerDevice]);

  return {
    showSpinner,
    deviceName,
    isPending: isRegistrationPending || showSpinner,
    showScannerSelectionModal,
    t,
    handleCloseScannerSelectionModal,
    handleChangeDeviceName,
    handleConfirm,
  };
};

export default useManageNameDevice;
